// eslint-disable-next-line no-unused-vars
import { EmploymentStatusEnum } from '../enums/EmploymentStatus.enum'

/**
 * Maps the provided address values to a address DTO.
 *
 * @param {Object} params - The input values for the address.
 * @param {string} params.city - The city of the address.
 * @param {string} params.country - The country of the address.
 * @param {string} params.postal - The postal code of the address.
 * @param {string} params.state - The state of the address.
 * @param {string} params.street1 - The primary street address (line 1).
 * @param {string} [params.street2] - The secondary street address (line 2, optional).
 *
 * @returns {Object} The address DTO.
 * @returns {string} return.city - The city of the address.
 * @returns {string} return.country - The country of the address.
 * @returns {string} return.postal - The postal code of the address.
 * @returns {string} return.state - The state of the address.
 * @returns {string[]} return.streets - An array of street lines for the address.
 */
export const toAddressDto = ({
  city,
  country,
  postal,
  state,
  street1,
  street2,
}) => ({
  city,
  country,
  postal,
  state,
  streets: street2 ? [street1, street2] : [street1],
})

/**
 * Maps the provided contact details values to a contact details DTO.
 *
 * @param {Object} params - The input values for the contact details.
 * @param {string} params.givenName - The given name of the contact.
 * @param {string} params.familyName - The family name of the contact.
 * @param {Object[]} params.phone - The array of phone objects.
 * @param {string} params.phone.phoneNumber - The phone number of the contact.
 * @param {string} params.phone.phoneType - The type of phone (e.g., 'HOME', 'WORK').
 * @param {string[]} params.email - The email addresses of the contact.
 *
 * @returns {Object} The contact details DTO.
 * @returns {string} return.givenName - The given name of the contact.
 * @returns {string} return.familyName - The family name of the contact.
 * @returns {Object[]} return.phone - An array containing phone details.
 * @returns {string} return.phone[].phoneType - The type of phone in the phone details array.
 * @returns {string} return.phone[].phoneNumber - The phone number in the phone details array.
 * @returns {string[]} return.email - An array containing the email address.
 */
export const toContactDetailsDto = ({
  givenName,
  familyName,
  phone,
  email,
}) => ({
  givenName,
  familyName,
  phone: phone ?? [],
  email: email ?? [],
})

/**
 * Maps the provided employment values to an employment DTO.
 *
 * @param {Object} params - The input values for the employment.
 * @param {EmploymentStatusEnum} params.employmentStatus - The employment status, represented by an enum value.
 * @param {string} params.employerName - The name of the employer.
 * @param {string} params.position - The position or job title.
 * @param {string} params.yearsEmployed - The number of years employed.
 * @param {Object} params.businessAddress - The business address object, returned by `toAddressDto`.
 * @param {string} params.businessAddress.city - The city of the address.
 * @param {string} params.businessAddress.country - The country of the address.
 * @param {string} params.businessAddress.postal - The postal code of the address.
 * @param {string} params.businessAddress.state - The state of the address.
 * @param {string[]} params.businessAddress.streets - An array of street lines for the address.
 *
 * @returns {Object} The employment DTO with mapped values.
 * @returns {EmploymentStatusEnum} return.employmentStatus - The employment status enum value.
 * @returns {string} return.employerName - The name of the employer.
 * @returns {string} return.position - The position or job title.
 * @returns {string} return.yearsEmployed - The number of years employed.
 * @returns {Object} return.businessAddress - The mapped business address object.
 * @returns {string} return.businessAddress.city - The city of the address.
 * @returns {string} return.businessAddress.country - The country of the address.
 * @returns {string} return.businessAddress.postal - The postal code of the address.
 * @returns {string} return.businessAddress.state - The state of the address.
 * @returns {string[]} return.businessAddress.streets - An array of street lines for the address.
 */
export const toEmploymentDto = ({
  employmentStatus,
  employerName,
  position,
  yearsEmployed,
  businessAddress,
}) => ({
  employmentStatus,
  employerName,
  position,
  yearsEmployed,
  businessAddress,
})

/**
 * Maps the provided investment profile values to an investment profile DTO.
 *
 * @param {Object} params - The input values for the investment profile.
 * @param {string} params.investmentExperience - The level of investment experience.
 * @param {string} params.riskTolerance - The risk tolerance of the investor.
 * @param {string} params.investmentObjective - The investment objective.
 * @param {number} params.maxTotalNetWorthUSD - The maximum total net worth in USD.
 * @param {number} params.maxAnnualIncomeUSD - The maximum annual income in USD.
 * @param {number} params.maxLiquidNetWorthUSD - The maximum liquid net worth in USD.
 * @param {number} params.minTotalNetWorthUSD - The minimum total net worth in USD.
 * @param {number} params.minAnnualIncomeUSD - The minimum annual income in USD.
 * @param {number} params.minLiquidNetWorthUSD - The minimum liquid net worth in USD.
 * @param {string} params.timeHorizon - The time horizon for the investments.
 * @param {string} params.liquidityNeeds - The liquidity needs of the investor.
 * @param {string} params.stockTradesPerYear - The stock trades per year.
 * @param {string} params.optionsTradesPerYear - The options trades per year.
 * @param {string} params.commonditiesTradesPerYear - The commodities trades per year.
 * @param {string} params.corporateBondsTradesPerYear - The corporate bonds trades per year.
 * @param {string} params.municipalBondsTradesPerYear - The municipal bonds trades per year.
 * @param {string} params.governmentBondsTradesPerYear - The government bonds trades per year.
 * @param {string} params.fundsTradesPerYear - The funds trades per year.
 * @param {string} params.marginAccountTradesPerYear - The margin account trades per year.
 * @param {Date} params.riskStandardOptions - The date of the risk standard options assessment.
 * @param {Date} params.uncoveredOptionWriters - The date of the uncovered option writers assessment.
 *
 * @returns {Object} The investment profile DTO with mapped values.
 * @returns {string} investmentExperience - The level of investment experience.
 * @returns {string} riskTolerance - The risk tolerance of the investor.
 * @returns {string} investmentObjective - The investment objective.
 * @returns {number} maxTotalNetWorthUSD - The maximum total net worth in USD.
 * @returns {number} maxAnnualIncomeUSD - The maximum annual income in USD.
 * @returns {number} maxLiquidNetWorthUSD - The maximum liquid net worth in USD.
 * @returns {number} minTotalNetWorthUSD - The minimum total net worth in USD.
 * @returns {number} minAnnualIncomeUSD - The minimum annual income in USD.
 * @returns {number} minLiquidNetWorthUSD - The minimum liquid net worth in USD.
 * @returns {string} timeHorizon - The time horizon for the investments.
 * @returns {string} liquidityNeeds - The liquidity needs of the investor.
 * @returns {string} stockTradesPerYear - The stock trades per year.
 * @returns {string} optionsTradesPerYear - The options trades per year.
 * @returns {string} commonditiesTradesPerYear - The commodities trades per year.
 * @returns {string} corporateBondsTradesPerYear - The corporate bonds trades per year.
 * @returns {string} municipalBondsTradesPerYear - The municipal bonds trades per year.
 * @returns {string} governmentBondsTradesPerYear - The government bonds trades per year.
 * @returns {string} fundsTradesPerYear - The funds trades per year.
 * @returns {string} marginAccountTradesPerYear - The margin account trades per year.
 * @returns {string} riskStandardOptions - The date of the risk standard options assessment.
 * @returns {string} uncoveredOptionWriters - The date of the uncovered option writers assessment.
 */
export const toInvestmentProfileDto = ({
  investmentExperience,
  riskTolerance,
  investmentObjective,
  maxTotalNetWorthUSD,
  maxAnnualIncomeUSD,
  maxLiquidNetWorthUSD,
  minTotalNetWorthUSD,
  minAnnualIncomeUSD,
  minLiquidNetWorthUSD,
  timeHorizon,
  liquidityNeeds,
  commonditiesTradesPerYear,
  corporateBondsTradesPerYear,
  municipalBondsTradesPerYear,
  governmentBondsTradesPerYear,
  fundsTradesPerYear,
  marginAccountTradesPerYear,
  riskStandardOptions,
  optionsTradesPerYear,
  uncoveredOptionWriters,
  stockTradesPerYear,
}) => ({
  investmentExperience,
  riskTolerance,
  investmentObjective,
  maxTotalNetWorthUSD,
  maxAnnualIncomeUSD,
  maxLiquidNetWorthUSD,
  minTotalNetWorthUSD,
  minAnnualIncomeUSD,
  minLiquidNetWorthUSD,
  timeHorizon,
  liquidityNeeds,
  commonditiesTradesPerYear,
  corporateBondsTradesPerYear,
  municipalBondsTradesPerYear,
  governmentBondsTradesPerYear,
  fundsTradesPerYear,
  marginAccountTradesPerYear,
  riskStandardOptions,
  optionsTradesPerYear,
  uncoveredOptionWriters,
  stockTradesPerYear,
})
